import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearch } from "wouter";

import { DeletePopUp } from "../../../components/PopUp";
import { useResponsive } from "../../../hooks/useResponsive";
import { RecurringTransactionsResponse } from "../../../services/recurringTransactions/types";
import { Transaction } from "../../../services/transactions";
import {
  getCurrencySymbol,
  getNumberFormat,
  TransactionType,
} from "../../../utils";
import { BREAKPOINTS } from "../../../utils/constants";
import { RecurringSection } from "../components/RecurringSection";
import { RecurringEditWarning } from "../components/RecurringEditWarning";
import { Insights } from "../components/Insights";
import { TransactionsFilters } from "../components/TransactionsFilters";
import {
  ALL_TIME_RANGE,
  MONTH_CHOICE_OPTIONS,
  RANGE_OPTIONS,
  SORT_OPTIONS,
  TYPE_OPTIONS,
} from "../components/TransactionsFilters/constants";
import { AccountEmptyState } from "../../../components/States";
import { CreateFab } from "../../../components/CreateFab";
import { TransactionsHero } from "../components/TransactionsHero";
import { TransactionsList } from "../components/TransactionsList";
import {
  usePeriodState,
  useRecurringTransactions,
  useTransactionFilters,
  useTransactionPanel,
  useTransactionsActions,
  useTransactionsList,
  useTransactionsModals,
  useTransactionsStaticData,
  useTransactionsTotals,
} from "../hooks";
import { TransactionsPanel } from "../panels";
import {
  getTitleFromAction,
  getTransactionObject,
} from "../utils/transformers";
import { PageWrapper } from "./styles";
import { TransactionsPageProps } from "./types";
import { cashbackBuysLabelOf, getDefaultFilters, idParamOf } from "./utils";

const TransactionsPage: React.FC<TransactionsPageProps> = () => {
  const isCompact = useResponsive(BREAKPOINTS.laptopMin);
  const showCreateFab = useResponsive(BREAKPOINTS.tabletLarge);
  const [, setLocation] = useLocation();
  const searchString = useSearch();

  const { filters, debouncedSearch, onValuesChange } = useTransactionFilters();

  const {
    accounts,
    categoriesIncome,
    categoriesExpense,
    accountOptions,
    categoryOptions,
    yearOptions,
    totalCount,
    isLoading: isLoadingStaticData,
  } = useTransactionsStaticData(filters.type.value);

  const searchParams = useMemo(
    () => new URLSearchParams(searchString),
    [searchString],
  );
  const accountIdParam = searchParams.get("accountId");
  const cashbackBuysOf = idParamOf(searchParams.get("cashbackBuysOf"));
  const cashbackBuysLabel = cashbackBuysLabelOf(accounts, cashbackBuysOf);
  const hasAppliedAccountParam = useRef(false);

  useEffect(() => {
    if (hasAppliedAccountParam.current || !accountIdParam) {
      return;
    }
    const matchedAccount = accountOptions.find(
      (option) => option.value === accountIdParam,
    );
    if (matchedAccount) {
      hasAppliedAccountParam.current = true;
      onValuesChange({ account: matchedAccount });
    }
  }, [accountIdParam, accountOptions, onValuesChange]);

  const periodState = usePeriodState(isCompact, yearOptions);
  const { onSelectRange } = periodState;

  // The buys span the account's whole history, and other filters would hide some of them.
  useEffect(() => {
    if (cashbackBuysOf === undefined) {
      return;
    }
    onValuesChange(getDefaultFilters());
    onSelectRange(ALL_TIME_RANGE);
  }, [cashbackBuysOf, onSelectRange, onValuesChange]);

  const onClearCashbackBuys = useCallback(() => {
    setLocation("/transactions");
  }, [setLocation]);

  const {
    totals,
    error: totalsError,
    refetchTotals,
  } = useTransactionsTotals({
    period: periodState.period,
    filters,
    debouncedSearch,
    isMonthPeriod: periodState.isMonthPeriod,
    cashbackBuysOf,
  });

  const {
    recurringTransactions,
    isLoadingRecurring,
    recurringError,
    refetchRecurring,
  } = useRecurringTransactions({ accountFilter: filters.account });

  const {
    transactions,
    pagination,
    isLoading: isLoadingList,
    isLoadingMore,
    error: listError,
    loadMore,
    refetch: refetchList,
  } = useTransactionsList({
    period: periodState.period,
    filters,
    debouncedSearch,
    isMonthPeriod: periodState.isMonthPeriod,
    cashbackBuysOf,
  });

  const { panel, openPanel, closePanel } = useTransactionPanel();
  const {
    deleteModal,
    deleteKind,
    showRecurringEditWarning,
    selectedTransaction,
    pendingRecurringEdit,
    openDeleteModal,
    closeDeleteModal,
    openRecurringEditWarning,
    closeRecurringEditWarning,
  } = useTransactionsModals();

  const {
    handleSubmit,
    handleDeleteConfirm,
    handleDeleteRecurring,
    handleEditRecurring,
    handleConfirmRecurringEdit,
  } = useTransactionsActions({
    accounts,
    panel,
    selectedTransaction,
    recurringTransactions,
    refetch: refetchList,
    refetchTotals,
    refetchRecurring,
    closePanel,
    closeDeleteModal,
  });

  const selectedType = filters.type.value;
  const showCategoryFilter = selectedType !== TransactionType.TRANSFER;

  const onAddTransaction = useCallback(() => {
    openPanel("create");
  }, [openPanel]);

  const onAddAccount = useCallback(() => {
    setLocation("/accounts");
  }, [setLocation]);

  const hasNoAccounts = !isLoadingStaticData && accounts.length === 0;
  // Insights read grouped totals and series, which the cashback buys filter does not narrow.
  const isInsightsShown = cashbackBuysOf === undefined;

  const onEditRecurring = useCallback(
    (recurringTransactionId: number) => {
      const recurring = handleEditRecurring(recurringTransactionId, openPanel);
      if (recurring) {
        openRecurringEditWarning(recurring);
      }
    },
    [handleEditRecurring, openPanel, openRecurringEditWarning],
  );

  const onDeleteRecurring = useCallback(
    (transaction: RecurringTransactionsResponse) => {
      openDeleteModal(transaction, "recurring");
    },
    [openDeleteModal],
  );

  const onEditTransaction = useCallback(
    (transaction: Transaction) => {
      openPanel("edit", getTransactionObject(transaction));
    },
    [openPanel],
  );

  const onDuplicateTransaction = useCallback(
    (transaction: Transaction) => {
      openPanel("duplicate", getTransactionObject(transaction));
    },
    [openPanel],
  );

  const onDeleteTransaction = useCallback(
    (transaction: Transaction) => {
      openDeleteModal(transaction, "regular");
    },
    [openDeleteModal],
  );

  const onConfirmDelete = useCallback(async () => {
    if (deleteKind === "regular") {
      await handleDeleteConfirm();
    } else {
      await handleDeleteRecurring();
    }
  }, [deleteKind, handleDeleteConfirm, handleDeleteRecurring]);

  const onConfirmRecurringEdit = useCallback(() => {
    handleConfirmRecurringEdit(pendingRecurringEdit, openPanel);
    closeRecurringEditWarning();
  }, [
    handleConfirmRecurringEdit,
    pendingRecurringEdit,
    openPanel,
    closeRecurringEditWarning,
  ]);

  const deleteMessage = useMemo(() => {
    if (!selectedTransaction) {
      return "";
    }
    const amountDisplay = `${getCurrencySymbol(selectedTransaction.currencyCode)}${getNumberFormat(selectedTransaction.amount)}`;
    if (deleteKind === "recurring") {
      return `Are you sure you want to delete this recurring transaction of ${amountDisplay}? This will not affect already created transactions.`;
    }
    return `Are you sure you want to delete this transaction of ${amountDisplay}?`;
  }, [deleteKind, selectedTransaction]);

  const deleteTitle =
    deleteKind === "recurring"
      ? "Delete Recurring Transaction"
      : "Delete Transaction";

  if (hasNoAccounts) {
    return (
      <PageWrapper>
        <AccountEmptyState
          onAddAccount={onAddAccount}
          title="Add your first account"
          description="Transactions show up here automatically once you have an account. Add one to start tracking your money."
          buttonLabel="Add account"
        />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <TransactionsHero
        periodLabel={periodState.periodLabel}
        totals={totals}
        error={totalsError}
        onAddTransaction={onAddTransaction}
        onResetPeriod={periodState.onResetPeriod}
        onRetry={refetchTotals}
      />

      <TransactionsFilters
        monthOptions={periodState.monthOptions}
        activeMonth={periodState.activeMonth}
        rangeValue={periodState.rangeValue}
        rangeOptions={RANGE_OPTIONS}
        yearOptions={yearOptions}
        yearValue={periodState.yearValue}
        monthChoiceOptions={MONTH_CHOICE_OPTIONS}
        monthChoiceValue={periodState.monthChoiceValue}
        showYear={periodState.showYear}
        showMonthChoice={periodState.showMonthChoice}
        onSelectMonth={periodState.onSelectMonth}
        onSelectRange={periodState.onSelectRange}
        onSelectYear={periodState.onSelectYear}
        onSelectMonthChoice={periodState.onSelectMonthChoice}
        filteredCount={totals.count}
        totalCount={totalCount}
        values={filters}
        typeOptions={TYPE_OPTIONS}
        showCategoryFilter={showCategoryFilter}
        categoryOptions={categoryOptions}
        accountOptions={accountOptions}
        sortOptions={SORT_OPTIONS}
        onValuesChange={onValuesChange}
        cashbackBuysLabel={cashbackBuysLabel}
        onClearCashbackBuys={onClearCashbackBuys}
      />

      {isInsightsShown && (
        <Insights
          period={periodState.period}
          filters={filters}
          debouncedSearch={debouncedSearch}
          isCompact={isCompact}
        />
      )}

      <RecurringSection
        recurringTransactions={recurringTransactions}
        isLoading={isLoadingRecurring}
        error={recurringError}
        onAdd={onAddTransaction}
        onEdit={onEditRecurring}
        onDelete={onDeleteRecurring}
        onRetry={refetchRecurring}
      />

      <TransactionsList
        transactions={transactions}
        pagination={pagination}
        isLoading={isLoadingList}
        isLoadingMore={isLoadingMore}
        error={listError}
        accounts={accounts}
        totalCount={totalCount}
        periodLabel={periodState.periodLabel}
        onLoadMore={loadMore}
        onRetry={refetchList}
        onEdit={onEditTransaction}
        onDuplicate={onDuplicateTransaction}
        onDelete={onDeleteTransaction}
      />

      {deleteModal && selectedTransaction && (
        <DeletePopUp
          isOpen={deleteModal}
          setIsOpen={closeDeleteModal}
          title={deleteTitle}
          text={deleteMessage}
          handleOnDelete={onConfirmDelete}
        />
      )}

      <RecurringEditWarning
        isOpen={showRecurringEditWarning}
        transaction={pendingRecurringEdit}
        onConfirm={onConfirmRecurringEdit}
        onCancel={closeRecurringEditWarning}
      />

      {panel.isOpen && (
        <TransactionsPanel
          title={getTitleFromAction(panel.action)}
          action={panel.action}
          isOpen={panel.isOpen}
          incomeCategoryList={categoriesIncome}
          expenseCategoryList={categoriesExpense}
          onClose={closePanel}
          transaction={panel.transaction}
          handleOnSubmit={handleSubmit}
          defaultAccount={null}
        />
      )}

      {showCreateFab && (
        <CreateFab onClick={onAddTransaction} ariaLabel="Add transaction" />
      )}
    </PageWrapper>
  );
};

export default TransactionsPage;
